import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Listen for the beforeinstallprompt event (Android/Desktop)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt if not dismissed before
      const dismissed = localStorage.getItem('pwa-prompt-dismissed');
      if (!dismissed) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Show iOS prompt if applicable
    if (iOS && !standalone) {
      const dismissed = localStorage.getItem('pwa-prompt-dismissed');
      if (!dismissed) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        setShowPrompt(false);
      }
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  // Don't show if already installed
  if (isStandalone || !showPrompt) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 animate-slide-up">
      <div className="max-w-md mx-auto bg-stone-800 rounded-2xl shadow-2xl border border-stone-700 overflow-hidden">
        <div className="p-4">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-red-500 rounded-xl flex-shrink-0">
              <Smartphone className="text-white" size={24} />
            </div>
            <div className="flex-grow">
              <h3 className="text-white font-bold text-lg">Install XRU Alert</h3>
              <p className="text-stone-300 text-sm mt-1">
                Get instant access to the community alert map on your home screen.
              </p>
            </div>
            <button 
              onClick={handleDismiss}
              className="text-stone-400 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {isIOS ? (
            <div className="mt-4 p-3 bg-stone-700/50 rounded-lg">
              <p className="text-stone-300 text-sm">
                <span className="font-semibold text-white">To install:</span> Tap the{' '}
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-stone-600 rounded text-xs">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-blue-400">
                    <path d="M12 2L12 14M12 2L8 6M12 2L16 6M4 14V20H20V14" stroke="currentColor" strokeWidth="2" fill="none"/>
                  </svg>
                  Share
                </span>{' '}
                button, then select{' '}
                <span className="font-semibold text-white">"Add to Home Screen"</span>
              </p>
            </div>
          ) : (
            <button
              onClick={handleInstall}
              className="mt-4 w-full py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <Download size={18} />
              Install App
            </button>
          )}
        </div>
        
        <div className="px-4 py-3 bg-stone-900/50 border-t border-stone-700">
          <p className="text-stone-400 text-xs text-center">
            Quick access • Works offline • No app store needed
          </p>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
